// ============================================
// SEAFARERS LIST PAGE - i18n
// ============================================

import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  MoreVertical,
  Eye,
  Edit,
  ShieldCheck,
  MapPin,
  Award,
  Ban,
  CheckCircle,
} from 'lucide-react';
import type { Seafarer, Department } from '@crew-at-sea/shared-types';
import { useTranslation } from '@crew-at-sea/i18n';
import {
  PageHeader,
  DataTable,
  FilterBar,
  FilterSelect,
  VerificationBadge,
  ConfirmDialog,
} from '@/components';
import { adminApi } from '@/services/api';
import { formatDate, getInitials, cn } from '@/utils';

export const Route = createFileRoute('/_authenticated/seafarers/')({
  component: SeafarersPage,
});

function SeafarersPage() {
  const { t } = useTranslation(['panel', 'common', 'enums']);
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [verifiedFilter, setVerifiedFilter] = useState('');
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    seafarer: Seafarer | null;
    action: 'suspend' | 'activate';
  }>({ open: false, seafarer: null, action: 'suspend' });

  // Denizcileri getir
  const { data: rawData, isLoading } = useQuery({
    queryKey: ['admin', 'seafarers', { page, limit, department: departmentFilter, search }],
    queryFn: async () => {
      const response = await adminApi.getSeafarers({ page, limit, department: departmentFilter || undefined, search: search || undefined });
      return (response as any)?.data || response;
    },
  });

  // Veri yapısını normalize et
  const data = rawData ? {
    data: rawData.data || [],
    meta: rawData.meta || { total: 0, page: 1, limit: 10, totalPages: 1, hasMore: false }
  } : { data: [], meta: { total: 0, page: 1, limit: 10, totalPages: 1, hasMore: false } };

  // Askıya Al/Aktifleştir mutation
  const statusMutation = useMutation({
    mutationFn: ({ userId, status, reason }: { userId: string; status: string; reason?: string }) =>
      adminApi.updateUserStatus(userId, { status: status as any, reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'seafarers'] });
      setConfirmDialog({ open: false, seafarer: null, action: 'suspend' });
    },
  });

  // Doğrulama mutation
  const verifyMutation = useMutation({
    mutationFn: ({ id, isVerified }: { id: string; isVerified: boolean }) =>
      adminApi.verifySeafarer(id, isVerified),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'seafarers'] });
    },
  });

  const handleConfirmAction = (reason?: string) => {
    if (!confirmDialog.seafarer) return;
    const newStatus = confirmDialog.action === 'suspend' ? 'SUSPENDED' : 'ACTIVE';
    statusMutation.mutate({ userId: confirmDialog.seafarer.userId, status: newStatus, reason });
  };

  const departmentColors: Record<string, string> = {
    DECK: 'bg-blue-100 text-blue-800',
    ENGINE: 'bg-green-100 text-green-800',
    ELECTRICAL: 'bg-amber-100 text-amber-800',
    CATERING: 'bg-purple-100 text-purple-800',
    OTHER: 'bg-gray-100 text-gray-800',
  };

  const columns = [
    {
      key: 'seafarer',
      header: t('panel:seafarers.seafarer'),
      render: (seafarer: Seafarer & { currentRank?: { nameTr: string } }) => (
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-ocean-100 text-ocean-700 font-medium">
            {seafarer.avatarUrl ? (
              <img
                src={seafarer.avatarUrl}
                alt={`${seafarer.firstName} ${seafarer.lastName}`}
                className="w-full h-full object-cover rounded-full"
              />
            ) : (
              getInitials(seafarer.firstName, seafarer.lastName)
            )}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <p className="font-medium text-gray-900">
                {seafarer.firstName} {seafarer.lastName}
              </p>
              {seafarer.isVerified && (
                <ShieldCheck className="h-4 w-4 text-green-500" />
              )}
            </div>
            <p className="text-xs text-gray-500">
              {seafarer.currentRank?.nameTr || t('panel:seafarers.noPosition')}
            </p>
          </div>
        </div>
      ),
    },
    {
      key: 'department',
      header: t('panel:seafarers.department'),
      render: (seafarer: Seafarer) => (
        <span
          className={cn(
            'px-2.5 py-0.5 text-xs font-medium rounded-full',
            departmentColors[seafarer.department] || departmentColors.OTHER
          )}
        >
          {t(`enums:department.${seafarer.department}`, { defaultValue: seafarer.department })}
        </span>
      ),
    },
    {
      key: 'experience',
      header: t('panel:seafarers.experience'),
      render: (seafarer: Seafarer) => (
        <div className="flex items-center gap-1 text-gray-600">
          <Award className="h-4 w-4 text-gray-400" />
          <span>{t('panel:seafarers.yearsExp', { count: seafarer.experienceYears })}</span>
        </div>
      ),
    },
    {
      key: 'seaDays',
      header: t('panel:seafarers.seaDays'),
      render: (seafarer: Seafarer) => (
        <span className="text-gray-600">{seafarer.totalSeaDays.toLocaleString('tr-TR')}</span>
      ),
    },
    {
      key: 'location',
      header: t('panel:seafarers.location'),
      render: (seafarer: Seafarer) => (
        <div className="flex items-center gap-1 text-gray-600">
          <MapPin className="h-4 w-4 text-gray-400" />
          <span>
            {seafarer.city && `${seafarer.city}, `}
            {seafarer.country || seafarer.nationality}
          </span>
        </div>
      ),
    },
    {
      key: 'profile',
      header: t('panel:seafarers.profileLabel'),
      render: (seafarer: Seafarer) => (
        <div className="flex items-center gap-2">
          <div className="w-20 h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className={cn(
                'h-full rounded-full',
                seafarer.profileCompleteness >= 80
                  ? 'bg-green-500'
                  : seafarer.profileCompleteness >= 50
                    ? 'bg-amber-500'
                    : 'bg-red-500'
              )}
              style={{ width: `${seafarer.profileCompleteness}%` }}
            />
          </div>
          <span className="text-xs text-gray-500">{seafarer.profileCompleteness}%</span>
        </div>
      ),
    },
    {
      key: 'verified',
      header: t('panel:seafarers.verification'),
      render: (seafarer: Seafarer) => (
        <VerificationBadge isVerified={seafarer.isVerified} />
      ),
    },
    {
      key: 'actions',
      header: '',
      width: '80px',
      render: (seafarer: Seafarer) => (
        <div className="relative group">
          <button className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md">
            <MoreVertical className="h-4 w-4" />
          </button>
          <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-maritime shadow-maritime-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
            <div className="py-1">
              <Link
                to={`/seafarers/${seafarer.id}` as any}
                className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                <Eye className="h-4 w-4" />
                {t('common:view')}
              </Link>
              <Link
                to={`/seafarers/${seafarer.id}/edit` as any}
                className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                <Edit className="h-4 w-4" />
                {t('common:edit')}
              </Link>
              <hr className="my-1" />
              {!seafarer.isVerified ? (
                <button
                  onClick={() => verifyMutation.mutate({ id: seafarer.id, isVerified: true })}
                  className="flex items-center gap-2 px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 w-full"
                >
                  <ShieldCheck className="h-4 w-4" />
                  {t('panel:seafarers.verify')}
                </button>
              ) : (
                <button
                  onClick={() => verifyMutation.mutate({ id: seafarer.id, isVerified: false })}
                  className="flex items-center gap-2 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 w-full"
                >
                  <ShieldCheck className="h-4 w-4" />
                  {t('panel:seafarers.unverify')}
                </button>
              )}
              {(seafarer as any).user?.status === 'SUSPENDED' ? (
                <button
                  onClick={() => setConfirmDialog({ open: true, seafarer, action: 'activate' })}
                  className="flex items-center gap-2 px-4 py-2 text-sm text-green-600 hover:bg-green-50 w-full"
                >
                  <CheckCircle className="h-4 w-4" />
                  {t('panel:seafarers.activate')}
                </button>
              ) : (
                <button
                  onClick={() => setConfirmDialog({ open: true, seafarer, action: 'suspend' })}
                  className="flex items-center gap-2 px-4 py-2 text-sm text-amber-600 hover:bg-amber-50 w-full"
                >
                  <Ban className="h-4 w-4" />
                  {t('panel:seafarers.suspend')}
                </button>
              )}
            </div>
          </div>
        </div>
      ),
    },
  ];

  const departmentOptions = [
    { value: 'DECK', label: t('enums:department.DECK') },
    { value: 'ENGINE', label: t('enums:department.ENGINE') },
    { value: 'ELECTRICAL', label: t('enums:department.ELECTRICAL') },
    { value: 'CATERING', label: t('enums:department.CATERING') },
    { value: 'OTHER', label: t('enums:department.OTHER') },
  ];

  const verifiedOptions = [
    { value: 'true', label: t('common:verified') },
    { value: 'false', label: t('common:unverified') },
  ];

  const activeFiltersCount =
    (departmentFilter ? 1 : 0) + (verifiedFilter ? 1 : 0) + (search ? 1 : 0);

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('panel:seafarers.title')}
        description={t('panel:seafarers.description')}
      />

      <FilterBar
        searchValue={search}
        onSearchChange={(val) => { setSearch(val); setPage(1); }}
        searchPlaceholder={t('panel:seafarers.searchPlaceholder')}
        filters={
          <>
            <FilterSelect
              value={departmentFilter}
              onChange={setDepartmentFilter}
              options={departmentOptions}
              placeholder={t('panel:seafarers.allDepartments')}
            />
            <FilterSelect
              value={verifiedFilter}
              onChange={setVerifiedFilter}
              options={verifiedOptions}
              placeholder={t('panel:seafarers.verificationStatus')}
            />
          </>
        }
        activeFiltersCount={activeFiltersCount}
        onClearFilters={() => {
          setSearch('');
          setDepartmentFilter('');
          setVerifiedFilter('');
        }}
      />

      <DataTable
        columns={columns}
        data={data?.data || []}
        keyExtractor={(seafarer) => seafarer.id}
        loading={isLoading}
        emptyMessage={t('panel:seafarers.noSeafarers')}
        pagination={{
          page,
          limit,
          total: data?.meta.total || 0,
          totalPages: data?.meta.totalPages || 1,
          onPageChange: setPage,
          onLimitChange: setLimit,
        }}
      />

      <ConfirmDialog
        open={confirmDialog.open}
        onOpenChange={(open) =>
          setConfirmDialog({ ...confirmDialog, open })
        }
        title={
          confirmDialog.action === 'suspend'
            ? t('panel:seafarers.suspendTitle')
            : t('panel:seafarers.activateTitle')
        }
        description={
          confirmDialog.action === 'suspend'
            ? t('panel:seafarers.suspendDescription', {
                name: `${confirmDialog.seafarer?.firstName} ${confirmDialog.seafarer?.lastName}`,
              })
            : t('panel:seafarers.activateDescription', {
                name: `${confirmDialog.seafarer?.firstName} ${confirmDialog.seafarer?.lastName}`,
              })
        }
        variant={confirmDialog.action === 'suspend' ? 'warning' : 'success'}
        confirmText={
          confirmDialog.action === 'suspend'
            ? t('panel:seafarers.suspend')
            : t('panel:seafarers.activate')
        }
        onConfirm={handleConfirmAction}
        loading={statusMutation.isPending}
        showReasonInput={confirmDialog.action === 'suspend'}
        reasonPlaceholder={t('panel:seafarers.suspendReasonPlaceholder')}
      />
    </div>
  );
}
